import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/brand/Logo";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [previewLink, setPreviewLink] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.preview_reset_token) {
        setPreviewLink(`${window.location.origin}/reset-password?token=${data.preview_reset_token}`);
      }
      toast.success("If the account exists, a reset link has been generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark-marketing grain min-h-screen flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="absolute top-6 left-6">
        <Link to="/"><Logo variant="light" /></Link>
      </div>
      <div className="w-full max-w-md glass rounded-sm p-8">
        <p className="editorial-overline">Reset password</p>
        <h1 className="font-serif text-3xl text-ivory-50 mt-3">Send me a reset link</h1>
        <p className="text-sm text-ivory-200/70 mt-2">Enter the email you used to sign up. If it matches an account, we'll issue a single-use reset link that expires in 30 minutes.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-ivory-200/60">Email</label>
            <input
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 w-full bg-transparent border border-ivory-200/20 px-4 py-3 text-ivory-50 rounded-sm focus:border-gold-500 outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit"
            className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-navy-900 font-semibold rounded-sm py-3 mt-2 transition-colors"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
        {sent && (
          <div className="mt-6 border border-ivory-200/10 rounded-sm p-4 text-sm text-ivory-200/80">
            <p>Check your inbox. If the address is registered, a reset link is on its way.</p>
            {previewLink && (
              <>
                <p className="mt-3 text-xs uppercase tracking-widest text-gold-500">Preview (no email provider configured yet)</p>
                <a href={previewLink} className="mt-1 block text-xs font-mono text-gold-400 break-all hover:text-gold-300" data-testid="forgot-preview-link">
                  {previewLink}
                </a>
              </>
            )}
          </div>
        )}
        <p className="text-xs text-ivory-200/60 mt-6">
          Remembered it? <Link to="/login" className="text-gold-500">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
